import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Divider, Button, Grid2 as Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { AnyAction } from 'redux';
import { connect, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import TabPanel from '../TabPanel';
import UserProfileEditor from './UserProfile/UserProfileEditor';
import UserPermissionsEditor from './UserPermissions/UserPermissionsEditor';
import UserDocumentsEditor from './UserDocuments/UserDocumentsEditor';
import { createUser, updateUser } from '../../store/users.actions';
import { initialPermissionValues } from './UserPermissions/permissionsData';
import { User, UserRole } from '../../model/types';
import { RootState } from '../../store/reducers';
import moment from 'moment';
import { dateToUtcISOString } from '../../helpers/dates';

const useStyles = makeStyles((theme: Theme) => ({
  userFormSubmitWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4.5),
  },
}));

// Note: create/update are local-only (see the TODO in users.actions.ts) —
// the API has no user-write endpoints, only GET /users.
const UserEditor = ({
  createUser,
  updateUser,
}: {
  createUser: (user: Partial<User>) => AnyAction;
  updateUser: (user: Partial<User> & { _id: string }) => AnyAction;
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const [value, setValue] = useState(0);
  const editorMode: 'edit' | 'create' = params.id ? 'edit' : 'create';

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const currentUser = useSelector((state: RootState) => {
    if (state.users.byId && params.id) {
      return state.users.byId[params.id];
    }

    return null;
  });

  const setInitialValues = () =>
    editorMode === 'edit' && currentUser
      ? {
          firstName: currentUser.firstName || '',
          lastName: currentUser.lastName || '',
          email: currentUser.email || '',
          phone: currentUser.phone || '',
          birthDate: currentUser.birthDate
            ? moment(currentUser.birthDate)
            : null,
          joinDate: currentUser.joinDate ? moment(currentUser.joinDate) : null,
          designation: currentUser.designation || '',
          department: currentUser.department || '',
          profileImageUrl: currentUser.profileImageUrl || '',
          password: '',
          permissions: currentUser.permissions || initialPermissionValues,
          documents: currentUser.documents || [],
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          birthDate: null,
          joinDate: null,
          designation: '',
          department: '',
          profileImageUrl: '',
          password: '',
          permissions: initialPermissionValues,
          documents: [],
          createRole: 'staff' as UserRole,
        };

  useEffect(() => {
    if (currentUser === null && params.id) {
      navigate('/users');
    }
  }, [currentUser, navigate, params.id]);

  return (
    <Formik
      initialValues={{ ...setInitialValues() }}
      validate={values => {
        if (editorMode === 'create' && !values.password) {
          return { password: t('Required') };
        }

        return {};
      }}
      onSubmit={(values, { resetForm }) => {
        const { createRole, ...restValues } = values;
        const user = {
          ...restValues,
          birthDate: restValues.birthDate
            ? dateToUtcISOString(restValues.birthDate.toDate())
            : null,
          joinDate: restValues.joinDate
            ? dateToUtcISOString(restValues.joinDate.toDate())
            : null,
        };

        if (!user.password) {
          delete user.password;
        }

        if (editorMode === 'edit' && params.id) {
          updateUser({ ...user, _id: params.id });
        } else {
          createUser({ ...user, roles: [createRole] });
          resetForm();
          navigate('/users');
        }
      }}
    >
      <Form>
        <Tabs
          value={value}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleChange}
          aria-label="Profile editor tabs"
        >
          <Tab label="Profile" />
          <Tab label="Security" />
          <Tab label="Documents" />
        </Tabs>
        <Divider orientation="horizontal" />
        <TabPanel index={0} value={value}>
          <UserProfileEditor />
        </TabPanel>
        <TabPanel index={1} value={value}>
          <UserPermissionsEditor editorMode={editorMode} />
        </TabPanel>
        <TabPanel index={2} value={value}>
          <UserDocumentsEditor />
        </TabPanel>
        <Grid container>
          <Grid
            className={classes.userFormSubmitWrapper}
            size={{ xs: 12, lg: 9 }}
          >
            <Button type="submit" variant="contained" color="primary">
              {editorMode === 'create' ? t('Add_User') : t('Save_Settings')}
            </Button>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

export default connect(null, {
  createUser,
  updateUser,
})(UserEditor);
